import type {
    EditAccountBusinessCardField as EditAccountBusinessCardFieldMutation,
    EditContactBusinessCardField as EditContactBusinessCardFieldMutation,
    PeepBusinessCardInfoFragment,
} from 'nutshell-graphql-types';

import type {PeepType} from '../../../../types';

function patchItemName<TValue extends {id: string; name: string}>(
    values: TValue[],
    attributeValueId: string,
    name: string
): TValue[] {
    return values.map((value) => {
        if (value.id !== attributeValueId) {
            return value;
        }

        return {
            ...value,
            name,
        };
    });
}

export function getOptimisticPeepForTitlePatch(
    peep: PeepBusinessCardInfoFragment,
    attributeValueId: string,
    name: string
): PeepBusinessCardInfoFragment {
    return {
        ...peep,
        emailAddresses: {
            ...peep.emailAddresses,
            all: patchItemName(peep.emailAddresses.all, attributeValueId, name),
        },
        phones: {
            ...peep.phones,
            all: patchItemName(peep.phones.all, attributeValueId, name),
        },
        postalAddresses: {
            ...peep.postalAddresses,
            all: patchItemName(peep.postalAddresses.all, attributeValueId, name),
        },
        urls: {
            ...peep.urls,
            all: patchItemName(peep.urls.all, attributeValueId, name),
        },
    };
}

// These declarations tell TypeScript that when callers pass
// 'account' they get the account mutation shape, and when they pass 'contact'
// they get the contact mutation shape (instead of a broad union).
export function getOptimisticEavPatchResponse(
    peep: PeepBusinessCardInfoFragment,
    attributeValueId: string,
    name: string,
    entityType: 'account'
): EditAccountBusinessCardFieldMutation;

export function getOptimisticEavPatchResponse(
    peep: PeepBusinessCardInfoFragment,
    attributeValueId: string,
    name: string,
    entityType: 'contact'
): EditContactBusinessCardFieldMutation;

export function getOptimisticEavPatchResponse(
    peep: PeepBusinessCardInfoFragment,
    attributeValueId: string,
    name: string,
    entityType: PeepType
): EditAccountBusinessCardFieldMutation | EditContactBusinessCardFieldMutation {
    const optimisticPeep = getOptimisticPeepForTitlePatch(peep, attributeValueId, name);

    if (entityType === 'account') {
        return {
            accountEavPatch: {
                __typename: 'AccountEavPatchPayload',
                account: {
                    ...optimisticPeep,
                    __typename: 'Account',
                },
            },
        };
    }

    return {
        contactEavPatch: {
            __typename: 'ContactEavPatchPayload',
            contact: {
                ...optimisticPeep,
                __typename: 'Contact',
            },
        },
    };
}
